import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import type { EditTextHandle } from '@/features/storefront/sections/SectionRenderer';
import {
  readEditableText,
  restoreEmphasis,
  singleLine,
  stripEmphasis,
} from '@/features/storefront/sections/inlineText';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';

/**
 * A piece of section copy the merchant can type into, right on the page.
 *
 * **Absent unless asked for.** With no handle - the shopper's storefront, and
 * every section of the customiser but the selected one - this renders its
 * children and nothing else: no wrapper element, no attributes, no listeners.
 * The preview and the live page stay the same markup, which is the promise
 * the whole renderer is built on.
 *
 * **Two states, two elements.** At rest the words render exactly as the
 * section draws them (emphasis in the display face and all), inside a focusable
 * box that says it can be edited. Focused, that box is swapped for a
 * plain-text `contentEditable` holding the words alone. Swapped rather than
 * toggled, because React cannot share an element with a browser's editing
 * engine: the engine rewrites the text nodes under it as the merchant types,
 * and React reconciling its own children into them afterwards is how an editor
 * throws "removeChild" in the middle of a keystroke.
 *
 * **One edit, not one per key.** Nothing is written until the merchant is done
 * - Enter, or clicking away - and then exactly once, through `onCommit`, which
 * the customiser turns into one step of its undo history. Esc puts the words
 * back as they were. Shift+Enter is a new line only where the field is a
 * paragraph; a heading is one line and Enter always finishes it.
 */
export interface EditableTextProps {
  /** From `SectionEnvironment.editText`; `null` renders the children alone. */
  handle: EditTextHandle | null;
  /** The stored value, emphasis markers included. */
  value: string;
  /** Which setting this is (`heading`, `cta_label`, ...), for its accessible name. */
  field: string;
  /** A paragraph rather than a line: Shift+Enter starts a new line. */
  multiline?: boolean;
  /** The value may carry `*emphasis*`; edited as the words alone. */
  emphasis?: boolean;
  /**
   * Keep clicks from reaching whatever is around the words - a button label
   * inside a link, where the click would otherwise follow it.
   */
  isolateClick?: boolean;
  /** The words as the section renders them at rest. */
  children: ReactNode;
}

export function EditableText({ handle, ...props }: EditableTextProps) {
  if (!handle || !handle.editable) return <>{props.children}</>;
  return <EditingSurface handle={handle} {...props} />;
}

/** Accessible names, by the setting being edited. Unknown fields read as "Text". */
const FIELD_LABELS: Record<string, string> = {
  heading: 'sectionEditor.inline.field.heading',
  subheading: 'sectionEditor.inline.field.subheading',
  cta_label: 'sectionEditor.inline.field.cta_label',
  secondary_cta_label: 'sectionEditor.inline.field.secondary_cta_label',
  badges: 'sectionEditor.inline.field.badges',
  body: 'sectionEditor.inline.field.body',
  text: 'sectionEditor.inline.field.text',
  link_label: 'sectionEditor.inline.field.link_label',
  title: 'sectionEditor.inline.field.title',
  subtitle: 'sectionEditor.inline.field.subtitle',
  quote: 'sectionEditor.inline.field.quote',
  author: 'sectionEditor.inline.field.author',
  role: 'sectionEditor.inline.field.role',
  question: 'sectionEditor.inline.field.question',
  answer: 'sectionEditor.inline.field.answer',
  caption: 'sectionEditor.inline.field.caption',
  stat: 'sectionEditor.inline.field.stat',
  date: 'sectionEditor.inline.field.date',
};

function EditingSurface({
  handle,
  value,
  field,
  multiline = false,
  emphasis = false,
  isolateClick = false,
  children,
}: EditableTextProps & { handle: EditTextHandle }) {
  const t = useT();
  const [editing, setEditingState] = useState(false);
  // The same flag, readable synchronously: Enter closes the box and the blur
  // that the unmount fires can arrive before React has re-rendered, and two
  // commits of one edit would be two undo steps.
  const open = useRef(false);
  const setEditing = (next: boolean) => {
    open.current = next;
    setEditingState(next);
  };
  const box = useRef<HTMLSpanElement>(null);
  // Set by Esc so the blur that follows it does not commit what was typed.
  const cancelled = useRef(false);
  // The words the box opens with, read by the effect below at the moment it
  // opens - not a dependency, because the value changing under an open box (an
  // undo from the keyboard elsewhere) must not wipe what is being typed.
  const opening = useRef('');
  opening.current = emphasis ? stripEmphasis(value) : value;

  // "Heading (on the page)", not "Heading": the settings panel has a field of
  // that name too, and a screen reader should tell the two apart.
  const label = t('sectionEditor.inline.on_page', {
    // An entry of a list of words (`badges.1`) is named by its list.
    field: t(FIELD_LABELS[field.replace(/\.\d+$/, '')] ?? 'sectionEditor.inline.field.text'),
  });
  const hint = multiline ? t('sectionEditor.inline.hint_multiline') : t('sectionEditor.inline.hint');

  useLayoutEffect(() => {
    if (!editing) return;
    const element = box.current;
    if (!element) return;
    element.textContent = opening.current;
    element.focus();
    // The caret at the end of the words, where a merchant clicking into a
    // heading expects to start typing.
    const selection = window.getSelection?.();
    if (selection && typeof document.createRange === 'function') {
      const range = document.createRange();
      range.selectNodeContents(element);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [editing]);

  function finish() {
    if (!open.current) return;
    setEditing(false);
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const element = box.current;
    if (!element) return;
    let typed = readEditableText(element);
    if (!multiline) typed = singleLine(typed);
    typed = typed.trim();
    const next = emphasis ? restoreEmphasis(value, typed) : typed;
    if (next !== value) handle.onCommit(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
    // The editor's own shortcuts - Delete removes a section, arrows move the
    // selection - must not fire while the merchant is typing a word.
    event.stopPropagation();
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelled.current = true;
      finish();
      return;
    }
    if (event.key === 'Enter' && !(multiline && event.shiftKey)) {
      event.preventDefault();
      finish();
    }
  }

  function onClick(event: MouseEvent<HTMLSpanElement>) {
    if (!isolateClick) return;
    event.stopPropagation();
    event.preventDefault();
  }

  const frame =
    'cursor-text rounded-sm outline-offset-4 hover:outline hover:outline-1 hover:outline-dashed focus:outline focus:outline-2';

  if (editing) {
    return (
      <span
        key="editing"
        ref={box}
        role="textbox"
        aria-label={label}
        aria-multiline={multiline || undefined}
        tabIndex={0}
        contentEditable="plaintext-only"
        suppressContentEditableWarning
        spellCheck
        onBlur={finish}
        onKeyDown={onKeyDown}
        onClick={onClick}
        className={cn(frame, 'inline-block min-w-[2ch]')}
        style={{ whiteSpace: multiline ? 'pre-wrap' : undefined }}
        data-inline-edit="editing"
      />
    );
  }

  return (
    <span
      key="rest"
      role="textbox"
      aria-label={label}
      aria-multiline={multiline || undefined}
      tabIndex={0}
      title={hint}
      onFocus={() => setEditing(true)}
      onClick={onClick}
      onKeyDown={(event) => {
        // Reached from the keyboard: Enter opens the box, as it would a field.
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          setEditing(true);
        }
      }}
      className={frame}
      data-inline-edit="rest"
    >
      {children}
    </span>
  );
}
